/**
 * ForecastPeriodSelector — Phase 05.1 Plan 05 Task 1; quick-261001-m3v.
 *
 * THE FIVE QUARTER TABS (John, 2026-10-01): Q1, Q2, Q3 and Q4 of the current
 * fiscal year, then Q1 of the next — "5 tabs on each page at all times". The
 * window is derived from `now` (shared/forecast/quarters), never hard-coded, so
 * on 1 Jan 2027 the bar reads Q1..Q4 FY2027 + Q1 FY2028 on its own.
 *
 * Closed quarters (the ones before the current quarter) are the classic
 * dark-blue tab with white text — Adeptia navy, the same chrome as the
 * dashboard's top bar — with a "Closed" marker, so a viewer can see at a glance
 * which quarters are history and which are the live forecast. The current and
 * next quarters keep the MainTabBar styling (UI-SPEC §"Period control"):
 *   - active   = text-ink-900 font-semibold border-b-2 border-brand-600 -mb-px
 *   - inactive = text-ink-500 hover:text-brand-600
 *   - min-h-[44px] a11y touch floor, overflow-x-auto on narrow screens
 *   - ← → arrow keys cycle the tabs (standard ARIA tablist pattern)
 *
 * Selection drives ?period=<label> (the page owns the deep-link) AND the
 * useForecast cache key. Single tab visible at a time.
 */
import { forecastQuarterWindow } from '@saledash/shared/forecast/quarters';

interface ForecastPeriodSelectorProps {
  /** The active quarter label, e.g. 'FY2026-Q4'. */
  activePeriod: string;
  onChange: (period: string) => void;
  /** Override "now" for tests. */
  now?: Date;
}

export function ForecastPeriodSelector({
  activePeriod,
  onChange,
  now,
}: ForecastPeriodSelectorProps): React.JSX.Element {
  const tabs = forecastQuarterWindow(now ?? new Date());

  function handleKeyDown(e: React.KeyboardEvent, currentIdx: number): void {
    if (e.key === 'ArrowRight') {
      const next = tabs[(currentIdx + 1) % tabs.length];
      if (next) onChange(next.label);
    } else if (e.key === 'ArrowLeft') {
      const prev = tabs[(currentIdx - 1 + tabs.length) % tabs.length];
      if (prev) onChange(prev.label);
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Forecast quarter"
      className="flex overflow-x-auto border-b border-ink-200"
      data-testid="forecast-period-selector"
    >
      {tabs.map((tab, idx) => {
        const isActive = tab.label === activePeriod;
        const isClosed = tab.state === 'closed';
        const tone = isClosed
          ? // Classic dark-blue tab, white text; the active one gets the brand
            // underline like every other active tab so the selected state reads
            // the same across the bar.
            [
              'bg-adeptia-navy text-white hover:bg-adeptia-navy-2 rounded-t-md',
              isActive ? 'font-semibold border-b-2 border-brand-500 -mb-px' : 'opacity-90',
            ].join(' ')
          : isActive
            ? 'text-ink-900 font-semibold border-b-2 border-brand-600 -mb-px'
            : 'text-ink-500 hover:text-brand-600';
        return (
          <button
            key={tab.label}
            role="tab"
            aria-selected={isActive}
            id={`forecast-period-${tab.label}`}
            onClick={() => {
              onChange(tab.label);
            }}
            onKeyDown={(e) => {
              handleKeyDown(e, idx);
            }}
            className={`flex-none transition-colors ${
              // quick-261001-nym — closed quarters are the smaller, quieter tab.
              isClosed ? 'min-h-[36px] self-end px-3 py-1.5 text-xs' : 'min-h-[44px] px-4 py-2 text-sm'
            } ${tone}`}
            data-active={isActive}
            data-state={tab.state}
            data-testid={`forecast-tab-${tab.label}`}
          >
            {tab.title}
            {isClosed && (
              <span className="ml-2 text-[0.65rem] font-semibold uppercase tracking-wider opacity-80">
                Closed
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
